import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ParticleField } from "@/components/site/ParticleField";
import { Logo } from "@/components/site/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { REGISTER } from "@/constants/testIds/auth";

export default function Inscription() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Le mot de passe doit contenir au moins 6 caractères.");
    if (form.password !== form.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await register({ name: form.name, email: form.email, company: form.company, password: form.password });
      toast.success("Votre espace client est prêt !");
      navigate("/espace-client");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Inscription impossible.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-night px-5 py-32" data-testid="register-page">
      <ParticleField className="absolute inset-0 h-full w-full" density={50} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size="h-14" />
          <h1 className="mt-6 font-display text-3xl text-white">Créer votre espace</h1>
          <p className="mt-2 text-white/60 font-body text-sm">Accédez au suivi de vos dossiers et à un support prioritaire.</p>
        </div>
        <form onSubmit={submit} className="rounded-3xl border border-gold/25 bg-navy/60 backdrop-blur-xl p-8">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white/80">Nom complet</Label>
              <Input id="name" data-testid={REGISTER.nameInput} value={form.name} onChange={set("name")} placeholder="Votre nom" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input id="email" type="email" data-testid={REGISTER.emailInput} value={form.email} onChange={set("email")} placeholder="vous@email.com" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
            </div>
            <div>
              <Label htmlFor="company" className="text-white/80">Entreprise <span className="text-white/40">(optionnel)</span></Label>
              <Input id="company" data-testid="register-company-input" value={form.company} onChange={set("company")} placeholder="Votre société" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password" className="text-white/80">Mot de passe</Label>
                <Input id="password" type="password" data-testid={REGISTER.passwordInput} value={form.password} onChange={set("password")} placeholder="••••••" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-white/80">Confirmer</Label>
                <Input id="confirm" type="password" data-testid={REGISTER.passwordConfirmInput} value={form.confirm} onChange={set("confirm")} placeholder="••••••" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
              </div>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid={REGISTER.submitButton} className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3.5 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>Créer mon espace <ArrowRight size={16} /></>}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-white/60 font-body">
          Déjà un compte ?{" "}
          <Link to="/connexion" data-testid={REGISTER.loginLink} className="text-gold font-semibold hover:underline">Se connecter</Link>
        </p>
      </motion.div>
    </section>
  );
}
